import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Check, ChevronDown, ChevronRight, SlidersHorizontal, Calendar, Flag, FolderPlus } from 'lucide-react';
import { useTasks, useProjects, useAppStore, groupByDue, PRIORITY_COLORS, localDateKey, type Task, type Priority } from '@flowfocus/core';
import { useTimer } from '../hooks/TimerContext';
import { TaskDetail } from '../components/TaskDetail';

const VIEWS = [
  { id: 'all', label: 'Todas' },
  { id: 'today', label: 'Hoy' },
  { id: 'tomorrow', label: 'Mañana' },
  { id: 'thisWeek', label: '7 días' },
  { id: 'planned', label: 'Planificado' },
  { id: 'highPriority', label: 'Prioridad' },
  { id: 'completed', label: 'Hechas' },
];

export function TasksPage({ uid }: { uid: string }) {
  const { filteredTasks, addTask, updateTask, deleteTask, toggleComplete } = useTasks(uid);
  const { projects, addProject } = useProjects(uid);
  const { activeView, setActiveView, tasks } = useAppStore();
  const timer = useTimer();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<Task | null>(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [projName, setProjName] = useState('');

  const isTagView = typeof activeView === 'string' && activeView.startsWith('tag:');
  const isProjectView = !isTagView && !VIEWS.some((v) => v.id === activeView);
  const activeProject = projects.find((p) => p.id === activeView);
  const allTags = Array.from(new Set(tasks.flatMap((t) => t.tags))).sort();
  const title = isTagView ? `#${(activeView as string).slice(4)}`
    : activeProject ? activeProject.name
    : VIEWS.find((v) => v.id === activeView)?.label ?? 'Tareas';
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const completedToday = tasks.filter((t) => t.completed && t.completedAt && t.completedAt >= todayStart.getTime());
  const pending = filteredTasks.filter((t) => !t.completed);

  const startFocus = (task: Task) => { timer.setTask(task.id); timer.start(task.id); navigate('/timer'); };

  const handleAddProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projName.trim()) return;
    const p = await addProject(projName.trim());
    setProjName(''); setShowAddProject(false);
    if (p) setActiveView(p.id);
  };

  const liveDetail = detail ? tasks.find((t) => t.id === detail.id) ?? detail : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '22px 26px 10px', maxWidth: 760, width: '100%', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', margin: 0 }}>{title}</h1>
          <span style={{ fontSize: 13, color: 'var(--text-mut)' }}>{pending.length} pendientes</span>
        </div>

        <div className="no-scrollbar" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 6 }}>
          {VIEWS.map((v) => <Chip key={v.id} active={activeView === v.id} onClick={() => setActiveView(v.id)}>{v.label}</Chip>)}
          {projects.map((p) => <Chip key={p.id} active={activeView === p.id} onClick={() => setActiveView(p.id)} dot={p.color}>{p.name}</Chip>)}
          {allTags.map((tag) => <Chip key={'tag:' + tag} active={activeView === 'tag:' + tag} onClick={() => setActiveView('tag:' + tag)}>#{tag}</Chip>)}
          <button className="icon-btn" style={{ width: 30, height: 30, flexShrink: 0 }} title="Nuevo proyecto" onClick={() => setShowAddProject((v) => !v)}>
            <FolderPlus size={15} />
          </button>
        </div>

        {showAddProject && (
          <form onSubmit={handleAddProject} style={{ marginTop: 8 }}>
            <input autoFocus className="input-bare" value={projName} onChange={(e) => setProjName(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setShowAddProject(false)} placeholder="Nombre del proyecto"
              style={{ background: 'var(--fill-2)', borderRadius: 9, padding: '9px 12px' }} />
          </form>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 26px 90px' }}>
        <div style={{ maxWidth: 760, width: '100%', margin: '0 auto' }}>
          <AddTask onAdd={(p) => addTask({
            name: p.name, priority: p.priority, tags: [], recurrence: 'none',
            dueDate: p.dueDate, estimatedPomodoros: p.pomodoros, completedPomodoros: 0,
            subtasks: [], completed: false, projectId: isProjectView ? (activeView as string) : undefined,
          })} />

          {pending.length === 0 && completedToday.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-mut)' }}>
              <Check size={34} strokeWidth={1.5} style={{ opacity: 0.4 }} />
              <p style={{ fontSize: 14, marginTop: 12 }}>Sin tareas en esta vista</p>
            </div>
          )}

          {(activeView === 'all' ? groupByDue(pending) : [{ id: 'flat', label: '', tasks: pending }]).map((g) => (
            <div key={g.id}>
              {g.label && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, margin: '22px 4px 8px', fontSize: 20, fontWeight: 700, letterSpacing: '0.01em', color: g.id === 'overdue' ? 'var(--danger)' : 'var(--text-pri)' }}>
                  {g.label}<span style={{ fontSize: 15, fontWeight: 400, color: 'var(--text-mut)' }}>{g.tasks.length}</span>
                </div>
              )}
              {g.tasks.length > 0 && <div className="list-group">
              {g.tasks.map((task) => (
                <TaskRow key={task.id} task={task} projectColor={projects.find((p) => p.id === task.projectId)?.color}
                  onToggle={() => toggleComplete(task.id)} onOpen={() => setDetail(task)} onPlay={() => startFocus(task)}
                  focusing={timer.taskId === task.id} />
              ))}
              </div>}
            </div>
          ))}

          {activeView !== 'completed' && completedToday.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setCompletedOpen((v) => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-mut)', fontSize: 15, fontWeight: 500, padding: '10px 4px 8px' }}>
                {completedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Completadas hoy ({completedToday.length})
              </button>
              {completedOpen && <div className="list-group">{completedToday.map((task) => (
                <TaskRow key={task.id} task={task} projectColor={projects.find((p) => p.id === task.projectId)?.color}
                  onToggle={() => toggleComplete(task.id)} onOpen={() => setDetail(task)} onPlay={() => {}} focusing={false} />
              ))}</div>}
            </div>
          )}
        </div>
      </div>

      {liveDetail && (
        <TaskDetail task={liveDetail} projects={projects} onClose={() => setDetail(null)}
          onUpdate={(c) => updateTask(liveDetail.id, c)} onDelete={() => deleteTask(liveDetail.id)} />
      )}
    </div>
  );
}

function Chip({ active, onClick, children, dot }: { active: boolean; onClick: () => void; children: React.ReactNode; dot?: string }) {
  return (
    <button onClick={onClick} className="chip" style={{ flexShrink: 0, padding: '7px 13px', fontSize: 13, cursor: 'pointer', border: 'none', background: active ? 'var(--primary)' : 'var(--fill)', color: active ? 'var(--on-primary)' : 'var(--text-sec)' }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? 'var(--on-primary)' : dot }} />}
      {children}
    </button>
  );
}

function formatDue(ts: number) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((ts - today.getTime()) / 86_400_000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Mañana';
  if (diff === -1) return 'Ayer';
  if (diff < -1) return `Hace ${Math.abs(diff)}d`;
  return new Date(ts).toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function TaskRow({ task, projectColor, onToggle, onOpen, onPlay, focusing }: {
  task: Task; projectColor?: string; onToggle: () => void; onOpen: () => void; onPlay: () => void; focusing: boolean;
}) {
  const [hover, setHover] = useState(false);
  const overdue = task.dueDate && task.dueDate < new Date().setHours(0, 0, 0, 0) && !task.completed;
  return (
    <div className="task-row" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 12px', borderRadius: 12, marginBottom: 3, background: focusing ? 'var(--primary-soft)' : hover ? 'var(--bg-hover)' : 'transparent', opacity: task.completed ? 0.5 : 1, transition: 'background 120ms' }}>
      <button onClick={onToggle} className="tap-44" aria-label={task.completed ? 'Marcar como pendiente' : 'Completar tarea'} style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', border: `1.5px solid ${task.completed ? 'var(--green)' : 'var(--text-mut)'}`, background: task.completed ? 'var(--green)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {task.completed && <Check size={12} color="#fff" strokeWidth={3} />}
      </button>
      {task.priority !== 'none' && <div style={{ width: 3, height: 26, borderRadius: 3, background: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onOpen}>
        <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: task.completed ? 'line-through' : 'none' }}>{task.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          {task.dueDate && <span style={{ fontSize: 11, color: overdue ? 'var(--danger)' : 'var(--text-mut)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} /> {formatDue(task.dueDate)}</span>}
          {projectColor && <span style={{ width: 6, height: 6, borderRadius: '50%', background: projectColor }} />}
          {task.subtasks.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-mut)' }}>{task.subtasks.filter((s) => s.completed).length}/{task.subtasks.length}</span>}
          {task.estimatedPomodoros > 0 && (
            <div style={{ display: 'flex', gap: 2 }}>
              {Array.from({ length: Math.min(task.estimatedPomodoros, 6) }).map((_, i) => (
                <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: i < task.completedPomodoros ? 'var(--primary)' : 'var(--fill)' }} />
              ))}
            </div>
          )}
        </div>
      </div>
      {!task.completed && (
        <button onClick={onPlay} className="icon-btn task-play" aria-label="Enfocar esta tarea" style={{ width: 32, height: 32, opacity: hover || focusing ? 1 : 0, background: focusing ? 'var(--primary)' : 'var(--fill)', color: focusing ? 'var(--on-primary)' : 'var(--primary)', transition: 'opacity 150ms' }} title="Enfocar esta tarea">
          <Play size={13} fill="currentColor" />
        </button>
      )}
    </div>
  );
}

function AddTask({ onAdd }: { onAdd: (p: { name: string; priority: Priority; dueDate?: number; pomodoros: number }) => void }) {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const [priority, setPriority] = useState<Priority>('none');
  const [pomodoros, setPomodoros] = useState(1);
  const [due, setDue] = useState<number | undefined>(undefined);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), priority, dueDate: due, pomodoros });
    setName(''); setPriority('none'); setPomodoros(1); setDue(undefined); setOpen(false);
  };
  const quick = (days: number | null) => {
    if (days === null) return setDue(undefined);
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + days); setDue(d.getTime());
  };

  return (
    <form onSubmit={submit} className="card" style={{ borderRadius: 13, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
        <button type="submit" className="icon-btn" style={{ width: 30, height: 30, flexShrink: 0, background: 'var(--primary)', color: 'var(--on-primary)' }}><Plus size={18} /></button>
        <input className="input-bare" value={name} onChange={(e) => setName(e.target.value)} placeholder="Añadir tarea…" />
        <button type="button" className="icon-btn" style={{ width: 30, height: 30, flexShrink: 0, background: open ? 'var(--primary-soft)' : 'transparent', color: open ? 'var(--primary)' : 'var(--text-mut)' }} onClick={() => setOpen((v) => !v)}><SlidersHorizontal size={15} /></button>
      </div>
      {open && (
        <div style={{ padding: '4px 14px 14px', borderTop: '0.5px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <Calendar size={15} color="var(--text-mut)" />
            {[{ l: 'Hoy', d: 0 }, { l: 'Mañana', d: 1 }, { l: '7 días', d: 7 }].map((o) => {
              const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + o.d);
              const on = due === d.getTime();
              return <button key={o.l} type="button" className="chip" onClick={() => quick(on ? null : o.d)} style={{ padding: '6px 11px', cursor: 'pointer', border: 'none', background: on ? 'var(--primary)' : 'var(--fill)', color: on ? 'var(--on-primary)' : 'var(--text-sec)' }}>{o.l}</button>;
            })}
            <input type="date" value={due ? localDateKey(due) : ''} onChange={(e) => setDue(e.target.value ? new Date(e.target.value + 'T00:00:00').getTime() : undefined)} style={{ border: 'none', background: 'var(--fill)', borderRadius: 8, padding: '5px 9px', fontSize: 12, color: 'var(--text-sec)', marginLeft: 'auto' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Flag size={15} color="var(--text-mut)" />
            {(['high', 'medium', 'low', 'none'] as Priority[]).map((p) => (
              <button key={p} type="button" onClick={() => setPriority(p)} style={{ width: 28, height: 28, borderRadius: '50%', border: priority === p ? `2px solid ${PRIORITY_COLORS[p]}` : '2px solid transparent', background: 'var(--fill)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Flag size={13} fill={PRIORITY_COLORS[p]} color={PRIORITY_COLORS[p]} />
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 12, color: 'var(--text-mut)' }}>Pomodoros</span>
              <button type="button" className="icon-btn" style={{ width: 26, height: 26 }} onClick={() => setPomodoros((v) => Math.max(1, v - 1))}>−</button>
              <span style={{ width: 16, textAlign: 'center', fontWeight: 600 }}>{pomodoros}</span>
              <button type="button" className="icon-btn" style={{ width: 26, height: 26 }} onClick={() => setPomodoros((v) => Math.min(20, v + 1))}>+</button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
